/**
 * Derivacoes PURAS da fila do comercial: filtros combinaveis com contagem,
 * ordenacao, SLA de primeira resposta por vendedora, ranking e o "o que fazer
 * agora". Fora do componente para a regra ser uma so (lista, cartoes e avisos
 * leem as mesmas funcoes) e para trocar pelo servidor sem mexer na tela.
 *
 * Decisoes que fogem do obvio:
 *  1. SLA = primeira resposta da vendedora em ate 15 min DEPOIS da mensagem do
 *     lead. Quem ainda espera conta como fora do SLA so depois de passar dos
 *     15 min -- antes disso ainda da tempo, e marcar vermelho seria alarme falso.
 *  2. As contagens de cada chip sao calculadas com os OUTROS filtros ativos
 *     (faceta): "Aguardando 3" com a vendedora X escolhida quer dizer 3 dela,
 *     nao 3 da empresa. Chip que promete um numero e entrega outro nao serve.
 *  3. A janela de comissao de cada atendimento e o toque que provocou a
 *     resposta + 7 dias (ADR-001). E prazo de PAGAMENTO, nao de registro: a
 *     venda paga dentro dela e registrada depois continua valendo.
 *  4. Toda ordenacao desempata pelo id: a mesma fila sempre na mesma ordem,
 *     sem linha pulando de lugar a cada atualizacao.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, formatRatio } from '../../lib/format';
import type { HandoffItem, HandoffStatus, Seller } from './types';
import { JANELA_DE_COMISSAO_MS, prazoCurto } from './vendaAssistida';

export const SLA_MINUTOS = 15;
export const VENDEDORA_TODAS = 'todas';
export const VENDEDORA_NENHUMA = '__sem_vendedora__';
const DIA_MIN = 1_440;

export type FiltroEstado = 'todos' | HandoffStatus;
export type OrdemDaFila = 'prioridade' | 'espera' | 'janela' | 'recentes' | 'valor';

export const ORDENS: readonly { readonly value: OrdemDaFila; readonly label: string; readonly dica: string }[] = [
  { value: 'prioridade', label: 'Prioridade', dica: 'Quem espera resposta primeiro (o mais antigo no topo), depois os atendimentos abertos, depois os encerrados.' },
  { value: 'espera', label: 'Mais tempo sem resposta', dica: 'Quem escreveu e ainda não ouviu a vendedora, do que espera há mais tempo para o mais recente.' },
  { value: 'janela', label: 'Janela de comissão acabando', dica: 'Atendimentos abertos cujo prazo de 7 dias desde o toque termina primeiro.' },
  { value: 'recentes', label: 'Respostas mais recentes', dica: 'Quem respondeu à régua por último aparece primeiro.' },
  { value: 'valor', label: 'Maior valor', dica: 'Carrinhos e planos mais caros primeiro.' },
];

export interface FiltrosDaFila {
  readonly estado: FiltroEstado;
  readonly vendedora: string;
  readonly busca: string;
}

export const FILTROS_INICIAIS: FiltrosDaFila = { estado: 'todos', vendedora: VENDEDORA_TODAS, busca: '' };

export function filtrosAtivos(f: FiltrosDaFila): number {
  return (f.estado === 'todos' ? 0 : 1) + (f.vendedora === VENDEDORA_TODAS ? 0 : 1) + (f.busca.trim() === '' ? 0 : 1);
}

// -----------------------------------------------------------------------------
// Tempo
// -----------------------------------------------------------------------------

export function estaAberto(item: HandoffItem): boolean {
  return item.status === 'aguardando' || item.status === 'em_atendimento';
}

export function minutosDesde(iso: string, agoraMs: number): number {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : Math.max(0, Math.round((agoraMs - t) / 60_000));
}

/** Aberto e sem primeira resposta da vendedora. */
export function esperandoResposta(item: HandoffItem): boolean {
  return estaAberto(item) && item.firstResponseMinutes === null;
}

/** Ha quanto tempo o lead espera a primeira resposta; `null` se ja foi respondido ou encerrou. */
export function minutosSemResposta(item: HandoffItem, agoraMs: number): number | null {
  return esperandoResposta(item) ? minutosDesde(item.repliedAt, agoraMs) : null;
}

export function fimDaJanelaMs(item: HandoffItem): number {
  return new Date(item.touchAt).getTime() + JANELA_DE_COMISSAO_MS;
}

export interface EstadoDaJanela {
  readonly terminaEm: string;
  readonly restanteMin: number;
  readonly tom: BadgeTone;
  readonly rotulo: string;
}

/** Prazo de comissao de um atendimento aberto; `null` quando ja encerrou. */
export function janelaDoAtendimento(item: HandoffItem, agoraMs: number): EstadoDaJanela | null {
  if (!estaAberto(item)) {
    return null;
  }
  const fim = fimDaJanelaMs(item);
  // Prazo que se esgota: arredonda para baixo (nunca prometer um minuto que nao existe).
  const restanteMin = Math.floor((fim - agoraMs) / 60_000);
  const terminaEm = new Date(fim).toISOString();
  if (restanteMin <= 0) {
    return { terminaEm, restanteMin, tom: 'critical', rotulo: 'janela vencida: venda agora não gera comissão' };
  }
  if (restanteMin < DIA_MIN) {
    return { terminaEm, restanteMin, tom: 'warning', rotulo: `comissão vale por mais ${prazoCurto(restanteMin)}` };
  }
  return { terminaEm, restanteMin, tom: 'neutral', rotulo: `comissão vale por mais ${prazoCurto(restanteMin)}` };
}

// -----------------------------------------------------------------------------
// Filtros e facetas
// -----------------------------------------------------------------------------

function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

/** Nome, oferta, referencia do pedido ou final do telefone (2+ digitos). */
export function combinaComBusca(item: HandoffItem, busca: string): boolean {
  const q = normalizar(busca);
  if (q === '') {
    return true;
  }
  const digitos = q.replace(/\D/g, '');
  if (digitos.length >= 2 && digitos.length === q.replace(/[\s()+-]/g, '').length) {
    return item.phoneE164.replace(/\D/g, '').endsWith(digitos);
  }
  return [item.leadName ?? '', item.offerName, item.orderRef ?? ''].some((campo) => normalizar(campo).includes(q));
}

export function combinaComVendedora(item: HandoffItem, vendedora: string): boolean {
  if (vendedora === VENDEDORA_TODAS) {
    return true;
  }
  return vendedora === VENDEDORA_NENHUMA ? item.sellerId === null : item.sellerId === vendedora;
}

export function filtrarFila(itens: readonly HandoffItem[], f: FiltrosDaFila): readonly HandoffItem[] {
  return itens.filter((i) => (f.estado === 'todos' || i.status === f.estado) && combinaComVendedora(i, f.vendedora) && combinaComBusca(i, f.busca));
}

export function contagemPorEstado(itens: readonly HandoffItem[], f: FiltrosDaFila): Readonly<Record<FiltroEstado, number>> {
  const base = itens.filter((i) => combinaComVendedora(i, f.vendedora) && combinaComBusca(i, f.busca));
  const conta = (s: HandoffStatus): number => base.filter((i) => i.status === s).length;
  return { todos: base.length, aguardando: conta('aguardando'), em_atendimento: conta('em_atendimento'), fechou: conta('fechou'), perdeu: conta('perdeu') };
}

/** Contagem por vendedora com os outros filtros ativos. Chaves: VENDEDORA_TODAS, VENDEDORA_NENHUMA e cada id. */
export function contagemPorVendedora(itens: readonly HandoffItem[], f: FiltrosDaFila, sellers: readonly Seller[]): ReadonlyMap<string, number> {
  const base = itens.filter((i) => (f.estado === 'todos' || i.status === f.estado) && combinaComBusca(i, f.busca));
  const mapa = new Map<string, number>([
    [VENDEDORA_TODAS, base.length],
    [VENDEDORA_NENHUMA, base.filter((i) => i.sellerId === null).length],
  ]);
  for (const s of sellers) {
    mapa.set(s.id, base.filter((i) => i.sellerId === s.id).length);
  }
  return mapa;
}

// -----------------------------------------------------------------------------
// Ordenacao
// -----------------------------------------------------------------------------

function porId(a: HandoffItem, b: HandoffItem): number {
  return a.id.localeCompare(b.id);
}

function maisRecente(a: HandoffItem, b: HandoffItem): number {
  return b.repliedAt.localeCompare(a.repliedAt) || porId(a, b);
}

export function ordenarFila(itens: readonly HandoffItem[], ordem: OrdemDaFila, agoraMs: number): readonly HandoffItem[] {
  const lista = [...itens];
  switch (ordem) {
    case 'prioridade': {
      const grupo = (i: HandoffItem): number => (esperandoResposta(i) ? 0 : estaAberto(i) ? 1 : 2);
      return lista.sort((a, b) => grupo(a) - grupo(b) || (grupo(a) === 0 ? a.repliedAt.localeCompare(b.repliedAt) || porId(a, b) : maisRecente(a, b)));
    }
    case 'espera': {
      const espera = (i: HandoffItem): number => minutosSemResposta(i, agoraMs) ?? -1;
      const primeira = (i: HandoffItem): number => i.firstResponseMinutes ?? -1;
      return lista.sort((a, b) => espera(b) - espera(a) || primeira(b) - primeira(a) || maisRecente(a, b));
    }
    case 'janela': {
      const chave = (i: HandoffItem): number => (estaAberto(i) ? fimDaJanelaMs(i) : Number.MAX_SAFE_INTEGER);
      return lista.sort((a, b) => chave(a) - chave(b) || maisRecente(a, b));
    }
    case 'recentes':
      return lista.sort(maisRecente);
    case 'valor':
      return lista.sort((a, b) => b.amountCents - a.amountCents || maisRecente(a, b));
    default: {
      const nunca: never = ordem;
      return nunca;
    }
  }
}

// -----------------------------------------------------------------------------
// SLA, ranking e resumo
// -----------------------------------------------------------------------------

export function tomDoSla(bps: number | null): BadgeTone {
  if (bps === null) {
    return 'neutral';
  }
  return bps >= 8_000 ? 'good' : bps >= 5_000 ? 'warning' : 'critical';
}

export interface SlaDaVendedora {
  readonly seller: Seller;
  readonly posicao: number;
  readonly abertos: number;
  readonly respondidos: number;
  readonly noSla: number;
  /** noSla / respondidos, em bps; `null` sem nenhuma resposta ainda. */
  readonly slaBps: number | null;
  readonly mediaMin: number | null;
  readonly fechadas: number;
  readonly perdidas: number;
  readonly valorFechadoCents: number;
  readonly comissaoCents: number;
}

function media(valores: readonly number[]): number | null {
  return valores.length === 0 ? null : Math.round(valores.reduce((a, b) => a + b, 0) / valores.length);
}

function comissaoCreditada(itens: readonly HandoffItem[]): number {
  return itens.reduce((acc, i) => acc + (i.status === 'fechou' && i.commissionState === 'creditada' ? i.commissionCents ?? 0 : 0), 0);
}

/** Ranking por valor fechado (desempate: SLA, depois nome). */
export function slaPorVendedora(itens: readonly HandoffItem[], sellers: readonly Seller[]): readonly SlaDaVendedora[] {
  const linhas = sellers.map((seller) => {
    const dela = itens.filter((i) => i.sellerId === seller.id);
    const respostas = dela.map((i) => i.firstResponseMinutes).filter((m): m is number => m !== null);
    const noSla = respostas.filter((m) => m <= SLA_MINUTOS).length;
    const fechadas = dela.filter((i) => i.status === 'fechou');
    return {
      seller,
      abertos: dela.filter(estaAberto).length,
      respondidos: respostas.length,
      noSla,
      slaBps: respostas.length === 0 ? null : Math.round((noSla * 10_000) / respostas.length),
      mediaMin: media(respostas),
      fechadas: fechadas.length,
      perdidas: dela.filter((i) => i.status === 'perdeu').length,
      valorFechadoCents: fechadas.reduce((acc, i) => acc + (i.closedAmountCents ?? 0), 0),
      comissaoCents: comissaoCreditada(dela),
    };
  });
  linhas.sort((a, b) => b.valorFechadoCents - a.valorFechadoCents || (b.slaBps ?? -1) - (a.slaBps ?? -1) || a.seller.name.localeCompare(b.seller.name, 'pt-BR'));
  return linhas.map((l, indice) => ({ ...l, posicao: indice + 1 }));
}

export interface ResumoDaFila {
  readonly aguardando: number;
  readonly emAtendimento: number;
  readonly fechadas: number;
  readonly perdidas: number;
  readonly valorFechadoCents: number;
  /** So vendas com comissao creditada (ADR-001). */
  readonly comissaoCents: number;
  readonly semComissao: number;
  readonly taxaFechamentoBps: number;
  readonly manuais: number;
  readonly respondidos: number;
  readonly noSla: number;
  readonly slaBps: number | null;
  readonly mediaRespostaMin: number | null;
  /** Esperando resposta ha mais de 15 min agora. */
  readonly esperandoForaDoSla: number;
  readonly esperandoForaDoSlaCents: number;
  readonly maiorEsperaMin: number | null;
  readonly abertosValorCents: number;
}

export function resumirFila(itens: readonly HandoffItem[], agoraMs: number): ResumoDaFila {
  const fechadas = itens.filter((i) => i.status === 'fechou');
  const perdidas = itens.filter((i) => i.status === 'perdeu');
  const decididas = fechadas.length + perdidas.length;
  const respostas = itens.map((i) => i.firstResponseMinutes).filter((m): m is number => m !== null);
  const noSla = respostas.filter((m) => m <= SLA_MINUTOS).length;
  const esperas = itens.map((i) => ({ i, m: minutosSemResposta(i, agoraMs) })).filter((e): e is { i: HandoffItem; m: number } => e.m !== null);
  const foraDoSla = esperas.filter((e) => e.m > SLA_MINUTOS);
  return {
    aguardando: itens.filter((i) => i.status === 'aguardando').length,
    emAtendimento: itens.filter((i) => i.status === 'em_atendimento').length,
    fechadas: fechadas.length,
    perdidas: perdidas.length,
    valorFechadoCents: fechadas.reduce((acc, i) => acc + (i.closedAmountCents ?? 0), 0),
    comissaoCents: comissaoCreditada(itens),
    semComissao: fechadas.filter((i) => i.commissionState !== 'creditada').length,
    taxaFechamentoBps: decididas === 0 ? 0 : Math.round((fechadas.length * 10_000) / decididas),
    manuais: fechadas.filter((i) => i.closedVia === 'manual').length,
    respondidos: respostas.length,
    noSla,
    slaBps: respostas.length === 0 ? null : Math.round((noSla * 10_000) / respostas.length),
    mediaRespostaMin: media(respostas),
    esperandoForaDoSla: foraDoSla.length,
    esperandoForaDoSlaCents: foraDoSla.reduce((acc, e) => acc + e.i.amountCents, 0),
    maiorEsperaMin: esperas.length === 0 ? null : Math.max(...esperas.map((e) => e.m)),
    abertosValorCents: itens.filter(estaAberto).reduce((acc, i) => acc + i.amountCents, 0),
  };
}

// -----------------------------------------------------------------------------
// O que fazer agora
// -----------------------------------------------------------------------------

export interface AcaoDaFila {
  readonly estado?: FiltroEstado;
  readonly vendedora?: string;
  readonly ordem?: OrdemDaFila;
}

export type TomDoAviso = 'alerta' | 'oportunidade' | 'projecao' | 'bom';

export interface AvisoDoComercial {
  readonly key: string;
  readonly tom: TomDoAviso;
  readonly icone: IconName;
  readonly titulo: string;
  readonly texto: string;
  readonly acao: AcaoDaFila | null;
  readonly rotuloAcao: string | null;
}

function plural(n: number, um: string, varios: string): string {
  return `${formatCount(n)} ${n === 1 ? um : varios}`;
}

/**
 * No maximo quatro avisos, do mais urgente ao menos. Todos partem de quem JA
 * respondeu a um toque da regua (pos-abandono): nada aqui sugere falar com
 * quem ainda nao saiu do checkout.
 */
export function avisosDoComercial(itens: readonly HandoffItem[], sellers: readonly Seller[], agoraMs: number, feeBps: number): readonly AvisoDoComercial[] {
  const r = resumirFila(itens, agoraMs);
  const lista: AvisoDoComercial[] = [];

  if (r.esperandoForaDoSla > 0) {
    lista.push({
      key: 'fora-do-sla',
      tom: 'alerta',
      icone: 'relogio',
      titulo: `${plural(r.esperandoForaDoSla, 'lead espera', 'leads esperam')} resposta há mais de ${SLA_MINUTOS} min`,
      texto: `Somam ${formatBRLFromCents(r.esperandoForaDoSlaCents)}${r.maiorEsperaMin === null ? '' : `; o mais antigo espera há ${prazoCurto(r.maiorEsperaMin)}`}. Quem escreve e não ouve ninguém esfria: atenda por ordem de espera.`,
      acao: { estado: 'aguardando', vendedora: VENDEDORA_TODAS, ordem: 'espera' },
      rotuloAcao: 'Ver quem espera há mais tempo',
    });
  }

  const abertos = itens.filter(estaAberto);
  const acabando = abertos.filter((i) => {
    const resta = fimDaJanelaMs(i) - agoraMs;
    return resta > 0 && resta <= DIA_MIN * 60_000;
  });
  const vencidas = abertos.filter((i) => fimDaJanelaMs(i) <= agoraMs).length;
  if (acabando.length > 0) {
    lista.push({
      key: 'janela-acabando',
      tom: 'alerta',
      icone: 'escudo',
      titulo: `${plural(acabando.length, 'atendimento perde', 'atendimentos perdem')} a comissão em menos de 24 h`,
      texto: `${formatBRLFromCents(acabando.reduce((acc, i) => acc + i.amountCents, 0))} em conversa. Se a pessoa já pagou por Pix ou por outro link, registre a venda com a data do pagamento: dentro dos 7 dias do toque a comissão vale.${vencidas > 0 ? ` Outros ${formatCount(vencidas)} já passaram do prazo.` : ''}`,
      acao: { estado: 'todos', ordem: 'janela' },
      rotuloAcao: 'Ver quem vence primeiro',
    });
  }

  const pior = slaPorVendedora(itens, sellers)
    .filter((s) => s.slaBps !== null && s.respondidos >= 3 && s.slaBps < 6_000)
    .sort((a, b) => (a.slaBps ?? 0) - (b.slaBps ?? 0))[0];
  if (pior !== undefined && pior.slaBps !== null) {
    lista.push({
      key: 'sla-vendedora',
      tom: 'oportunidade',
      icone: 'usuarios',
      titulo: `${pior.seller.name} respondeu ${formatPercentFromBps(pior.slaBps)} dentro de ${SLA_MINUTOS} min`,
      texto: `Primeira resposta média de ${pior.mediaMin === null ? '—' : prazoCurto(pior.mediaMin)}; a equipe está em ${r.slaBps === null ? '—' : formatPercentFromBps(r.slaBps)}. Mande os próximos para quem responde mais rápido até a fila dela baixar.`,
      acao: { estado: 'todos', vendedora: pior.seller.id, ordem: 'espera' },
      rotuloAcao: `Ver a fila de ${pior.seller.name.split(/\s+/)[0] ?? pior.seller.name}`,
    });
  }

  if (abertos.length > 0 && r.taxaFechamentoBps > 0) {
    const projecao = Math.round((r.abertosValorCents * r.taxaFechamentoBps) / 10_000);
    // Comissao so do que ainda esta dentro dos 7 dias do toque (ADR-001): conversa
    // com janela vencida pode virar venda, mas nao gera comissao.
    const naJanelaCents = abertos.filter((i) => fimDaJanelaMs(i) > agoraMs).reduce((acc, i) => acc + i.amountCents, 0);
    const projecaoNaJanela = Math.round((naJanelaCents * r.taxaFechamentoBps) / 10_000);
    lista.push({
      key: 'projecao',
      tom: 'projecao',
      icone: 'grafico',
      titulo: `≈ ${formatBRLFromCents(projecao)} ainda podem fechar`,
      texto: `${plural(abertos.length, 'conversa aberta soma', 'conversas abertas somam')} ${formatBRLFromCents(r.abertosValorCents)}; na taxa de fechamento do período (${formatRatio(r.fechadas, r.fechadas + r.perdidas)}), devem virar ≈ ${formatBRLFromCents(projecao)}, com ≈ ${formatBRLFromCents(Math.round((projecaoNaJanela * feeBps) / 10_000))} de comissão na taxa atual${vencidas > 0 ? ` (só o que ainda está dentro dos 7 dias do toque)` : ''}.`,
      acao: { estado: 'em_atendimento', vendedora: VENDEDORA_TODAS, ordem: 'valor' },
      rotuloAcao: 'Ver as maiores',
    });
  }

  if (lista.every((a) => a.tom !== 'alerta')) {
    lista.unshift({
      key: 'em-dia',
      tom: 'bom',
      icone: 'check',
      titulo: 'Fila em dia',
      texto: `Ninguém espera resposta há mais de ${SLA_MINUTOS} min e nenhuma janela de comissão vence nas próximas 24 h.`,
      acao: null,
      rotuloAcao: null,
    });
  }

  return lista.slice(0, 4);
}
